import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, RotateCcw, CheckCircle2, Undo2 } from "lucide-react";
import { Card, EmptyState, Loader } from "@/components/ui-helpers";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const caseLabel = (a) => `${a.service_type || "Case"} · ${a.client_name || "Client"}`;

const restoreTokens = async (tokens, onRestored) => {
  try {
    for (const t of tokens) await api.post(`/deleted-items/${t}/restore`);
    toast.success(tokens.length > 1 ? `${tokens.length} cases restored` : "Case restored");
    onRestored?.();
  } catch (e) { toast.error(apiErr(e)); }
};

// Bin button + confirmation; moves one or many cases to Recently Deleted with a 10s Undo
export function DeleteCasesButton({ cases, onDeleted, onRestored, variant = "icon", testid }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const many = cases.length > 1;
  const run = async () => {
    setBusy(true);
    try {
      const tokens = many
        ? (await api.post("/applications/bulk-delete", { ids: cases.map((c) => c.id) })).data.undo_tokens
        : [(await api.delete(`/applications/${cases[0].id}`)).data.undo_token];
      setOpen(false);
      toast.success(many ? `${tokens.length} cases moved to Recently Deleted` : "Case moved to Recently Deleted", {
        duration: 10000, action: { label: "Undo", onClick: () => restoreTokens(tokens, onRestored) },
      });
      onDeleted?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const trigger = variant === "icon" ? (
    <button type="button" data-testid={testid} title="Delete case" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
      className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 className="h-4 w-4" /></button>
  ) : variant === "bulk" ? (
    <button type="button" data-testid={testid} onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 px-4 py-2 rounded-lg text-sm font-semibold"><Trash2 className="h-4 w-4" /> Delete selected</button>
  ) : (
    <button type="button" data-testid={testid} onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-rose-50 text-rose-600 hover:bg-rose-100"><Trash2 className="h-4 w-4" /> Delete</button>
  );
  return (
    <>
      {trigger}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid="delete-case-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{many ? `Delete ${cases.length} cases?` : `Delete ${caseLabel(cases[0] || {})}?`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-500">
                {many && <ul className="max-h-32 overflow-y-auto list-disc pl-5 text-slate-700">{cases.map((c) => <li key={c.id}>{caseLabel(c)}</li>)}</ul>}
                <p>{many ? "Their" : "Its"} tasks, documents and payment records go to <b>Recently Deleted</b> along with {many ? "them" : "it"}. You can restore everything from there.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-case-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-case-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); run(); }} className="bg-rose-600 hover:bg-rose-700">{busy ? "Deleting…" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// "Restore to Active" / "Restore to Closed" pair for archived cases
export function RestoreChoices({ onPick, size = "sm", idSuffix = "" }) {
  const cls = size === "sm" ? "px-2 py-1 text-[11px]" : "px-3.5 py-2.5 text-sm";
  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
      <button type="button" data-testid={`restore-active${idSuffix}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPick("active"); }}
        className={`inline-flex items-center gap-1 rounded-lg font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 whitespace-nowrap ${cls}`}><RotateCcw className="h-3.5 w-3.5" /> Restore to Active</button>
      <button type="button" data-testid={`restore-completed${idSuffix}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPick("completed"); }}
        className={`inline-flex items-center gap-1 rounded-lg font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 whitespace-nowrap ${cls}`}><CheckCircle2 className="h-3.5 w-3.5" /> Restore to Closed</button>
    </div>
  );
}

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// Recently Deleted cases with restore (and permanent removal for admins)
export function RecentlyDeletedCases({ onChanged }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const load = () => api.get("/deleted-items?kind=application").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const purge = async (it) => {
    if (!window.confirm(`Permanently delete ${it.label}? This cannot be undone.`)) return;
    try { await api.delete(`/deleted-items/${it.id}`); toast.success("Deleted for good"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  if (!items) return <Loader />;
  if (!items.length) return <Card className="p-6"><EmptyState icon={Trash2} title="Recently Deleted is empty" description="Deleted cases land here with their tasks, documents and payments, ready to restore." /></Card>;
  return (
    <Card className="divide-y divide-slate-100" data-testid="recently-deleted-list">
      {items.map((it) => (
        <div key={it.id} data-testid={`deleted-case-${it.id}`} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 truncate">{it.label}</div>
            <div className="text-xs text-slate-400">Deleted {when(it.deleted_at)}{it.deleted_by_name ? ` by ${it.deleted_by_name}` : ""}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid={`deleted-restore-${it.id}`} onClick={() => restoreTokens([it.id], () => { load(); onChanged?.(); })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100"><Undo2 className="h-3.5 w-3.5" /> Restore</button>
            {user?.role === "admin" && <button data-testid={`deleted-purge-${it.id}`} onClick={() => purge(it)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-rose-600 text-xs font-semibold hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /> Delete forever</button>}
          </div>
        </div>
      ))}
    </Card>
  );
}
